/**
 * usePod — context for pod membership and sharing preferences.
 *
 * Supports multiple pods. `pod` and `members` always refer to the first
 * pod the user belongs to. `allMembers` aggregates across all pods.
 *
 * Backed by PocketBase collections 'pods' / 'pod_members'.
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@clerk/react';
import PodService from '../services/PodService.js';
import { useCurrentUser } from './useCurrentUser.jsx';

const PodContext = createContext({
    pods: [],
    pod: null,
    members: [],
    allMembers: [],
    myMembership: null,
    loading: true,
    allowedUsers: () => [],
    allowedListUsers: () => [],
    sharedListIds: () => [],
    createPod: async () => {},
    joinPod: async () => {},
    updateSharing: async () => {},
    leavePod: async () => {},
    removeMember: async () => {},
    transferOwnership: async () => {},
    refresh: async () => {},
});

const APP_SHARE_KEYS = {
    videogames: 'shareGames',
    movies: 'shareMovies',
    seasons: 'shareMovies',
    books: 'shareBooks',
    boardgames: 'shareBoardgames',
};

export function PodProvider({ children }) {
    const currentUser = useCurrentUser();
    const { getToken } = useAuth();
    const [pods, setPods] = useState([]);
    const [loading, setLoading] = useState(true);
    const serviceRef = useRef(null);

    // Create/update the service when user changes
    useEffect(() => {
        if (currentUser?.id) {
            serviceRef.current = new PodService(currentUser.id, getToken);
        }
    }, [currentUser?.id, getToken]);

    const fetchPods = useCallback(async () => {
        if (!serviceRef.current) return;
        try {
            const data = await serviceRef.current.getMyPods();
            setPods(data.pods || []);
        } catch (err) {
            console.error('Failed to fetch pods:', err);
            setPods([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (currentUser?.id) {
            serviceRef.current = new PodService(currentUser.id, getToken);
            fetchPods();
        } else {
            setLoading(false);
        }
    }, [currentUser?.id, getToken, fetchPods]);

    // First pod record (used for activity association)
    const pod = pods[0]?.pod || null;
    const members = pods[0]?.members || [];

    // All members across all pods (deduplicated by userId)
    const allMembers = useMemo(() => {
        const seen = new Set();
        const result = [];
        for (const p of pods) {
            for (const m of (p.members || [])) {
                if (!seen.has(m.userId)) {
                    seen.add(m.userId);
                    result.push(m);
                }
            }
        }
        return result;
    }, [pods]);

    const myMembership = useMemo(
        () => members.find(m => m.userId === currentUser?.id) || null,
        [members, currentUser?.id]
    );

    /**
     * Returns user IDs whose collection data is visible for a given app.
     * Always includes the current user. Aggregates across ALL pods —
     * any member who shares this app in any pod the user belongs to is included.
     */
    const allowedUsers = useCallback((appKey) => {
        if (!currentUser) return null;
        const idSet = new Set([currentUser.id]);

        const shareField = APP_SHARE_KEYS[appKey];
        if (!shareField) return [...idSet];

        for (const p of pods) {
            for (const m of (p.members || [])) {
                if (m.userId === currentUser.id) continue;
                if (m[shareField] === 'all') {
                    idSet.add(m.userId);
                }
            }
        }
        return [...idSet];
    }, [currentUser, pods]);

    /**
     * Returns user IDs whose lists are visible for a given app.
     * Aggregates across ALL pods. Includes users with share level 'all' or 'lists'.
     */
    const allowedListUsers = useCallback((appKey) => {
        if (!currentUser) return [];
        const idSet = new Set([currentUser.id]);

        const shareField = APP_SHARE_KEYS[appKey];
        if (!shareField) return [...idSet];

        for (const p of pods) {
            for (const m of (p.members || [])) {
                if (m.userId === currentUser.id) continue;
                if (m[shareField] === 'all' || m[shareField] === 'lists') {
                    idSet.add(m.userId);
                }
            }
        }
        return [...idSet];
    }, [currentUser, pods]);

    /**
     * Returns the specific list IDs a member has chosen to share.
     * Empty array means "share all lists".
     */
    const sharedListIds = useCallback((memberId) => {
        const m = members.find(mem => mem.id === memberId);
        return m?.sharedLists || [];
    }, [members]);

    const createPod = useCallback(async (name) => {
        if (!serviceRef.current) throw new Error('Not signed in — cannot create pod');
        await serviceRef.current.createPod(name);
        await fetchPods();
    }, [fetchPods]);

    const joinPod = useCallback(async (inviteCode) => {
        if (!serviceRef.current) throw new Error('Not signed in — cannot join pod');
        await serviceRef.current.joinPod(inviteCode);
        await fetchPods();
    }, [fetchPods]);

    const updateSharing = useCallback(async (memberId, prefs) => {
        if (!serviceRef.current) throw new Error('Not signed in — cannot update sharing');
        await serviceRef.current.updateSharing(memberId, prefs);
        await fetchPods();
    }, [fetchPods]);

    const leavePod = useCallback(async (memberId) => {
        if (!serviceRef.current) throw new Error('Not signed in — cannot leave pod');
        await serviceRef.current.leavePod(memberId);
        await fetchPods();
    }, [fetchPods]);

    const removeMember = useCallback(async (memberId) => {
        if (!serviceRef.current) throw new Error('Not signed in — cannot remove member');
        await serviceRef.current.removeMember(memberId);
        await fetchPods();
    }, [fetchPods]);

    const transferOwnership = useCallback(async (memberId) => {
        if (!serviceRef.current) throw new Error('Not signed in — cannot transfer ownership');
        await serviceRef.current.transferOwnership(memberId);
        await fetchPods();
    }, [fetchPods]);

    const updatePod = useCallback(async (podId, updates) => {
        if (!serviceRef.current) throw new Error('Not signed in — cannot update pod');
        await serviceRef.current.updatePod(podId, updates);
        await fetchPods();
    }, [fetchPods]);

    const getPendingRequests = useCallback(async () => {
        if (!serviceRef.current) throw new Error('Not signed in — cannot fetch requests');
        return serviceRef.current.getPendingRequests();
    }, []);

    const reviewRequest = useCallback(async (requestId, action, podId) => {
        if (!serviceRef.current) throw new Error('Not signed in — cannot review request');
        return serviceRef.current.reviewRequest(requestId, action, podId);
    }, []);

    const value = useMemo(() => ({
        pods,
        pod,
        members,
        allMembers,
        myMembership,
        loading,
        allowedUsers,
        allowedListUsers,
        sharedListIds,
        createPod,
        joinPod,
        updatePod,
        updateSharing,
        leavePod,
        removeMember,
        transferOwnership,
        getPendingRequests,
        reviewRequest,
        refresh: fetchPods,
    }), [pods, pod, members, allMembers, myMembership, loading, allowedUsers, allowedListUsers, sharedListIds, createPod, joinPod, updatePod, updateSharing, leavePod, removeMember, transferOwnership, getPendingRequests, reviewRequest, fetchPods]);

    // Don't render children until we know the current user's pod status,
    // otherwise adapters would briefly load with no user-filtering (showing all data).
    if (loading) {
        return (
            <div className="min-h-screen bg-bg-primary flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-text-tertiary border-t-accent-primary rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <PodContext.Provider value={value}>
            {children}
        </PodContext.Provider>
    );
}

export function usePod() {
    return useContext(PodContext);
}

export default usePod;
